import { useEffect, useRef, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Box,
  InputAdornment,
  TextField,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import dateFunctions from "../../utils/formatCalendarDate";
import "./calendarInput.css";

interface CalendarInputProps {
  value?: [string, string];
  onChange?: (dates: [string, string]) => void;
  onDateChange?: (from: string, to: string) => void;
  onDateTypingChange?: (from: string, to: string) => void;
  img?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  fullWidth?: boolean;
  placeholder?: string;
}

export const CalendarInput = ({
  value,
  onChange,
  img,
  onDateChange,
  onDateTypingChange,
  isOpen,
  setIsOpen,
  fullWidth = false,
  placeholder = "Período",
}: CalendarInputProps) => {
  const [range, setRange] = useState<DateRange | undefined>();
  const [isFocused, setIsFocused] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const calendarRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const [fromValue, toValue] = value ?? [];

    if (!fromValue?.trim() || !toValue?.trim()) {
      setRange(undefined);
      setInputValue("");
      return;
    }

    const from = dayjs(fromValue, "DD-MM-YYYY");
    const to = dayjs(toValue, "DD-MM-YYYY");

    if (!from.isValid() || !to.isValid()) {
      setRange(undefined);
      setInputValue("");
      return;
    }

    setRange({ from: from.toDate(), to: to.toDate() });
    setInputValue(
      `${from.format("DD/MM/YYYY")} - ${to.format("DD/MM/YYYY")}`
    );
  }, [value]);

  useEffect(() => {
    if (range?.from && range?.to && onDateChange) {
      const fromDate = dayjs(range.from).format("DD-MM-YYYY");
      const toDate = dayjs(range.to).format("DD-MM-YYYY");
      onDateChange(fromDate, toDate);
    }
  }, [range, onDateChange]);

  const handleSelect = (selected: DateRange | undefined) => {
    setRange(selected);
    if (onChange && selected?.from && selected?.to) {
      onChange([
        dayjs(selected.from).format("DD-MM-YYYY"),
        dayjs(selected.to).format("DD-MM-YYYY"),
      ]);
    }
  };

  const customTheme = createTheme({
    palette: {
      primary: {
        main: "#000000",
      },
    },
  });

  const formatters = {
    formatWeekdayName: (day: Date) => {
      const diasSemana = ["D", "S", "T", "Q", "Q", "S", "S"];
      return diasSemana[day.getDay()];
    },
    formatCaption: (date: Date) => {
      const meses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      return meses[date.getMonth()] + " " + date.getFullYear();
    },
  };

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(dayjs(date));
    const previousButton = document.querySelector(
      ".rdp-button_previous"
    ) as HTMLElement;
    previousButton?.classList.add("active");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (range?.from && range?.to) {
      setInputValue(
        `${dateFunctions.formatDate(range.from)} - ${dateFunctions.formatDate(
          range.to
        )}`
      );
    }
  }, [range]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={customTheme}>
        <Box display="flex" flexDirection="column" gap={2} className={fullWidth ? "calendar-full-width" : ""}>
          <TextField
            id="filled-basic"
            placeholder={placeholder}
            variant="filled"
            value={inputValue}
            onChange={(e) => {
              const raw = e.target.value;
              const masked = dateFunctions.applyDateRangeMask(raw);
              setInputValue(masked);
              const fullMatch = masked.match(
                /^(\d{2})\/(\d{2})\/(\d{4}) - (\d{2})\/(\d{2})\/(\d{4})$/
              );
              if (fullMatch) {
                const from = dayjs(
                  `${fullMatch[3]}-${fullMatch[2]}-${fullMatch[1]}`
                );
                const to = dayjs(
                  `${fullMatch[6]}-${fullMatch[5]}-${fullMatch[4]}`
                );
                const newRange = { from: from.toDate(), to: to.toDate() };
                setRange(newRange);
                if (onChange) {
                  onChange([
                    from.format("DD-MM-YYYY"),
                    to.format("DD-MM-YYYY"),
                  ]);
                }
                if (onDateChange) {
                  onDateChange(
                    from.format("DD-MM-YYYY"),
                    to.format("DD-MM-YYYY")
                  );
                }
                if (onDateTypingChange) {
                  onDateTypingChange(
                    from.format("DD-MM-YYYY"),
                    to.format("DD-MM-YYYY")
                  );
                }
              } else {
                setRange(undefined);
                if (onDateTypingChange) {
                  const [fromRaw, toRaw] = masked.split(" - ");
                  onDateTypingChange(fromRaw || "", toRaw || "");
                }
              }
            }}
            onClick={() => setIsOpen(!isOpen)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              "& > :not(style)": { width: "19.3ch" },
              "& .MuiFilledInput-root": {
                borderRadius: "0px",
                backgroundColor: isFocused ? "#E0E2E5" : "",
                fontSize: "15px",
                fontWeight: 500,
                height: "50px",
              },
              "& .MuiInputLabel-root": {
                fontWeight: "500",
                fontSize: "20px",
              },
              "& .MuiInputBase-input": {
                transform: "translateY(-10%)",
              },
            }}
            focused
            InputProps={{
              endAdornment: img ? (
                <InputAdornment position="end">
                  <img
                    src={img}
                    alt="icon"
                    style={{
                      width: "20px",
                      height: "20px",
                      objectFit: "contain",
                    }}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
          {isOpen && (
            <div ref={calendarRef}>
              <DayPicker
                mode="range"
                selected={range}
                onSelect={handleSelect}
                formatters={formatters}
                month={currentMonth.toDate()}
                onMonthChange={handleMonthChange}
              />
            </div>
          )}
        </Box>
      </ThemeProvider>
    </LocalizationProvider>
  );
};
